import { format, parse } from "date-fns";
import { uniq } from "lodash";
import { ReactNode } from "react";

import { DateFormat } from "constants/date";
import { ReportDistribution } from "constants/reports/distribution";
import { getSpecificDateFromRelative } from "helpers/date";
import { getFormattedNumberValue } from "helpers/string";
import { IChartSeries } from "store/views/reports";

import { IBreakdownTableProps } from "../../Breakdown/BreakdownTable";
import { getDateRangeLabel, getHourRangeLabel, getMonthRangeLabel } from "../chart-legend";

import { getDateRangeColumns, getHoursColumns } from "./columns-parser";
import { getFixedColumns } from "./fixed-columns";
import { IExtendedColumn } from "./interfaces";
import { getDateRangeData, getHoursData } from "./row-data-parser";
import { combineSimilarSeries, getUniqueDateRanges } from "./utils";

const distributionToLabelerMap = {
  [ReportDistribution.Day]: getDateRangeLabel,
  [ReportDistribution.Hour]: getHourRangeLabel,
  [ReportDistribution.Month]: getMonthRangeLabel,
};

export function getBreakdownPropsFromChartOptions(
  {
    series,
    xAxis: { categories },
    forceOptions,
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  }: { series: IChartSeries[]; xAxis: { categories: any }; forceOptions: any },
  valueParser: (value: unknown) => ReactNode,
  disableTotal = false
): Partial<IBreakdownTableProps> | null {
  if (!series || !series.length) {
    return null;
  }

  const now = new Date();
  const seriesWithSpecificDates = series.map((singleSeries: IChartSeries) => ({
    ...singleSeries,

    filters: {
      ...singleSeries.filters,
      dateFrom:
        singleSeries.filters?.dateFrom && singleSeries.filters.dateFrom instanceof Date
          ? format(singleSeries.filters.dateFrom, DateFormat.HtmlInputDate)
          : format(
              getSpecificDateFromRelative(singleSeries.filters?.dateFrom as string, now),
              DateFormat.HtmlInputDate
            ),
      dateTo:
        singleSeries.filters?.dateTo && singleSeries.filters.dateTo instanceof Date
          ? format(singleSeries.filters.dateTo, DateFormat.HtmlInputDate)
          : format(getSpecificDateFromRelative(singleSeries.filters?.dateTo as string, now), DateFormat.HtmlInputDate),
    },
  }));

  const distributions =
    (forceOptions && forceOptions.distribution && [forceOptions.distribution]) ||
    uniq(seriesWithSpecificDates.map((s: IChartSeries) => s.filters && s.filters.distribution));

  if (distributions.length !== 1) {
    return null;
  }

  const columns: IExtendedColumn[] = getFixedColumns(seriesWithSpecificDates, forceOptions?.fixedSeriesHeader);
  const seriesData = [];
  const distribution = distributions[0] as ReportDistribution;
  const uniqueDateRanges = getUniqueDateRanges(seriesWithSpecificDates);
  const dateRanges = uniqueDateRanges.map(dateRangeString => dateRangeString.split("_"));
  const [from, to] = dateRanges[0].map(date => parse(date));
  const isEvenFixedColumnsCount = columns.length % 2 === 0;

  if (distribution === ReportDistribution.Hour) {
    const labels = categories.map((category: { label: string }) => category.label);
    columns.push(...getHoursColumns(uniqueDateRanges, labels, isEvenFixedColumnsCount, valueParser));
    seriesData.push(...getHoursData(seriesWithSpecificDates, labels));
  } else {
    columns.push(
      ...getDateRangeColumns(
        seriesWithSpecificDates,
        uniqueDateRanges,
        distribution,
        isEvenFixedColumnsCount,
        valueParser
      )
    );
    seriesData.push(...getDateRangeData(seriesWithSpecificDates, distribution));
  }

  if (!seriesData.length) {
    return null;
  }

  const data = combineSimilarSeries(seriesData);
  const aggregationHeader = distributionToLabelerMap[distribution](from, to);
  if (!disableTotal) {
    const totalClassName = `${columns.length % 2 === 0 ? "even" : "odd"}`;
    columns.push({
      Header: <strong>Total</strong>,
      accessor: "total",
      className: totalClassName,
      headerClassName: totalClassName,
      Cell: ({ value }) => (valueParser ? valueParser(value) : getFormattedNumberValue(value)),
    });
  }
  if (
    series.every(
      (singleSeries: IChartSeries) => singleSeries.extras && singleSeries.extras.customAggregate !== undefined
    )
  ) {
    const customAggregateClassName = `aggregate-column ${columns.length % 2 === 0 ? "even" : "odd"}`;
    columns.push({
      Header: aggregationHeader,
      accessor: "customAggregate",
      className: customAggregateClassName,
      headerClassName: customAggregateClassName,
      Cell: ({ value }) => (valueParser ? valueParser(value) : getFormattedNumberValue(value)),
      width: 160,
    });
  }

  return {
    columns,
    data,
  };
}
